/**
 * melonDS WASM Loader
 * Handles loading and initialization of the melonDS WebAssembly module
 */

export interface WasmModule {
  _malloc: (size: number) => number;
  _free: (ptr: number) => void;
  _melonInit: () => number;
  _melonLoadROM: (romPtr: number, romSize: number) => number;
  _melonReset: () => void;
  _melonRunFrame: () => void;
  _melonGetFramebuffer: () => number;
  _melonGetSaveRAM: (sizePtr: number) => number;
  _melonSetSaveRAM: (dataPtr: number, size: number) => void;
  _melonGetAudioBuffer: () => number;
  _melonGetAudioBufferSize: () => number;
  _melonSetTouchPos: (x: number, y: number) => void;
  _melonReleaseTouchPos: () => void;
  _melonSetButtonState: (button: number, pressed: number) => void;
  HEAP8: Int8Array;
  HEAPU8: Uint8Array;
  HEAP16: Int16Array;
  HEAPU16: Uint16Array;
  HEAP32: Int32Array;
  HEAPU32: Uint32Array;
  HEAPF32: Float32Array;
  HEAPF64: Float64Array;
}

export class WasmLoader {
  private module: WasmModule | null = null;
  private isLoaded = false;
  private isLoading = false;

  /**
   * Load the melonDS WASM module
   */
  async load(): Promise<WasmModule> {
    if (this.module) {
      return this.module;
    }

    if (this.isLoading) {
      // Wait for ongoing load
      while (this.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (this.module) {
        return this.module;
      }
    }

    this.isLoading = true;

    try {
      // Check if SharedArrayBuffer is available (required for melonDS threading)
      if (typeof SharedArrayBuffer === 'undefined') {
        throw new Error(
          'SharedArrayBuffer is not available. This usually means the page is not cross-origin isolated. ' +
          'The server must send the following headers:\n' +
          '- Cross-Origin-Embedder-Policy: require-corp\n' +
          '- Cross-Origin-Opener-Policy: same-origin\n\n' +
          'If you are on localhost, make sure your development server is configured correctly. ' +
          'On production, check your hosting platform configuration (Vercel, Netlify, etc.).'
        );
      }
      console.log('✓ SharedArrayBuffer is available');
      // Load WASM module
      // Note: This assumes melonDS WASM files are in /wasm/ directory
      // You'll need to replace this with the actual melonDS WASM loader

      // For now, this is a placeholder that will be replaced when
      // we have the actual melonDS WASM build

      const wasmPath = '/wasm/melonds.wasm';
      const scriptPath = '/wasm/melonds.js';

      // Configure Module BEFORE loading the script (Emscripten requirement)
      // @ts-expect-error - Emscripten Module global
      window.Module = {
        locateFile: (path: string) => {
          console.log('📍 Locating file:', path);
          if (path.endsWith('.wasm')) {
            console.log('  → Resolved to:', wasmPath);
            return wasmPath;
          }
          const resolved = '/wasm/' + path;
          console.log('  → Resolved to:', resolved);
          return resolved;
        },
        onRuntimeInitialized: null as any,
        print: (text: string) => console.log('📢 WASM stdout:', text),
        printErr: (text: string) => console.error('📢 WASM stderr:', text),
        onAbort: (what: any) => {
          console.error('💥 WASM ABORTED:', what);
          throw new Error(`WASM module aborted: ${what}`);
        },
      };

      // Create promise for module initialization with timeout
      const moduleReadyPromise = new Promise<WasmModule>((resolve, reject) => {
        // Set a timeout for WASM initialization
        const timeout = setTimeout(() => {
          reject(new Error(
            'WASM module initialization timed out after 30 seconds. ' +
            'This may indicate:\n' +
            '1. The WASM file is corrupted or incompatible\n' +
            '2. Network issues loading the WASM file\n' +
            '3. Browser compatibility issues\n' +
            '4. Missing COOP/COEP headers\n\n' +
            'Check the browser console for more details.'
          ));
        }, 30000); // 30 second timeout

        // @ts-expect-error - Emscripten Module
        window.Module.onRuntimeInitialized = () => {
          clearTimeout(timeout);
          console.log('✓ WASM runtime initialized');
          // @ts-expect-error - Emscripten Module
          resolve(window.Module as WasmModule);
        };
      });

      // Load the glue code script (this will auto-init the Module)
      console.log('📥 Loading WASM script from:', scriptPath);
      await this.loadScript(scriptPath);
      console.log('✓ WASM script loaded, waiting for runtime initialization...');

      // Wait for Module to be ready (with timeout)
      this.module = await moduleReadyPromise;

      // Debug: List all exported functions
      console.log('✓ Available WASM functions:', Object.keys(this.module).filter(k => typeof (this.module as any)[k] === 'function'));
      console.log('✓ Available WASM properties:', Object.keys(this.module).slice(0, 50));

      // Debug: Inspect WasmEmulator class
      if ((this.module as any).WasmEmulator) {
        const emulatorClass = (this.module as any).WasmEmulator;
        console.log('✓ WasmEmulator type:', typeof emulatorClass);
        console.log('✓ WasmEmulator prototype:', Object.getOwnPropertyNames(emulatorClass.prototype || {}));

        // Try to create an instance to see its methods
        try {
          const testInstance = new emulatorClass();
          console.log('✓ WasmEmulator instance methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(testInstance)));
          // Don't keep the test instance
          if (testInstance.delete) testInstance.delete();
        } catch (e) {
          console.log('ℹ WasmEmulator constructor needs parameters:', e);
        }
      }

      this.isLoaded = true;
      this.isLoading = false;

      if (!this.module) {
        throw new Error('Failed to initialize WASM module');
      }

      return this.module;
    } catch (error) {
      this.isLoading = false;
      console.error('Failed to load melonDS WASM:', error);
      throw new Error(`WASM loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Load a script dynamically
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Check if WASM is loaded
   */
  isModuleLoaded(): boolean {
    return this.isLoaded && this.module !== null;
  }

  /**
   * Get the loaded module
   */
  getModule(): WasmModule {
    if (!this.module) {
      throw new Error('WASM module not loaded. Call load() first.');
    }
    return this.module;
  }

  /**
   * Unload the module (cleanup)
   */
  unload(): void {
    this.module = null;
    this.isLoaded = false;
  }
}

// Singleton instance
export const wasmLoader = new WasmLoader();
